import { Form, TextField } from '../components/forms';
/**
 * FORGOT PASSWORD PAGE
 *
 * Request a password reset email.
 */

import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  BlockStack,
  Text,
  Banner,
  Box,
  Button,
} from '@shopify/polaris';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.whenlyapp.com';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim() }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        setError(result.error || 'Failed to send reset email');
        return;
      }

      setSuccess(true);
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [email]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f6f6f7',
      padding: '20px',
    }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>
        <BlockStack gap="600">
          {/* Logo/Brand */}
          <Box paddingBlockEnd="400">
            <BlockStack gap="200" align="center">
              <Text as="h1" variant="headingXl" alignment="center">
                Whenly
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
                Delivery Date & Warehouse Management
              </Text>
            </BlockStack>
          </Box>

          {/* Success Message */}
          {success ? (
            <Card>
              <BlockStack gap="400">
                <Banner tone="success">
                  <Text as="p" variant="bodyMd">
                    If an account exists with that email, you'll receive a reset link or sign-in instructions shortly.
                  </Text>
                </Banner>
                <Text as="p" variant="bodyMd">
                  Check your inbox and spam folder. Password reset links expire in 1 hour.
                  If you use Google, choose Continue with Google on the sign-in page.
                </Text>
                <Box paddingBlockStart="200">
                  <Link
                    to="/login"
                    style={{
                      color: '#2C6ECB',
                      textDecoration: 'none',
                      fontWeight: 500,
                    }}
                  >
                    Back to sign in
                  </Link>
                </Box>
              </BlockStack>
            </Card>
          ) : (
            <>
              {/* Error Banner */}
              {error && (
                <Banner tone="critical" onDismiss={() => setError(null)}>
                  {error}
                </Banner>
              )}

              {/* Form Card */}
              <Card>
                <BlockStack gap="500">
                  <Text as="h2" variant="headingMd">
                    Reset your password
                  </Text>
                  <Text as="p" variant="bodyMd" tone="subdued">
                    Enter your email address for a reset link or Google sign-in instructions.
                  </Text>

                  <Form onSubmit={handleSubmit}>
                    <BlockStack gap="400">
                      <TextField
                        label="Email"
                        type="email"
                        requiredIndicator value={email}
                        onChange={setEmail}
                        autoComplete="email"
                        disabled={isSubmitting}
                        autoFocus
                      />
                      <Button
                        submit
                        variant="primary"
                        size="large"
                        fullWidth
                        loading={isSubmitting}
                        disabled={!email.trim()}
                      >
                        Send reset link
                      </Button>
                    </BlockStack>
                  </Form>

                  <Box paddingBlockStart="200">
                    <Text as="p" variant="bodyMd" alignment="center">
                      Remember your password?{' '}
                      <Link
                        to="/login"
                        style={{
                          color: '#2C6ECB',
                          textDecoration: 'none',
                          fontWeight: 500,
                        }}
                      >
                        Sign in
                      </Link>
                    </Text>
                  </Box>
                </BlockStack>
              </Card>
            </>
          )}

          {/* Help Text */}
          <Text as="p" variant="bodySm" tone="subdued" alignment="center">
            Need help? Contact{' '}
            <a href="mailto:support@whenly.app" style={{ color: '#2C6ECB' }}>
              support@whenly.app
            </a>
          </Text>
        </BlockStack>
      </div>
    </div>
  );
}
